import { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, ArrowRight, Loader2, Building2, Crown, Palette, Shield, Headset } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIGHLIGHTS = [
  { icon: Crown, color: "text-amber-500", label: "Full Clinic OS suite", detail: "Hub+ + every add-on activated from day one." },
  { icon: Palette, color: "text-violet-500", label: "Whitelabel option", detail: "Make it feel like your own product. $297/mo per workspace · multiple accounts under one." },
  { icon: Shield, color: "text-blue-500", label: "Security & SLAs", detail: "SOC 2, custom DPAs, 99.95% uptime SLA, custom retention." },
  { icon: Headset, color: "text-emerald-500", label: "Dedicated CSM", detail: "Named success manager, custom integrations, priority support." },
];

export const EnterpriseModal = ({ onClose }) => {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    locations: "", whitelabel: "no", message: "",
  });
  const [submitState, setSubmitState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) return;
    setSubmitState("loading");
    setError("");

    const message = `[ENTERPRISE INQUIRY] Locations: ${form.locations || "not specified"} · Whitelabel: ${form.whitelabel === "yes" ? "Yes — interested" : "No"}${form.message ? ` · Notes: ${form.message}` : ""}`;
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        clinic_name: form.company.trim(),
        phone: form.phone.trim() || "—",
        message,
      });
      setSubmitState("success");
    } catch (err) {
      setSubmitState("error");
      setError(err?.response?.data?.detail?.[0]?.msg || "Couldn't submit — please try again.");
    }
  };

  return (
    <div
      data-testid="enterprise-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/45 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        {/* Top accent */}
        <div className="h-[3px] w-full bg-gradient-to-r from-amber-500 via-violet-500 to-blue-500" />

        {/* Decorative glow */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-amber-500/15 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-border/70 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm shadow-amber-500/30">
              <Crown className="h-4 w-4" />
            </span>
            <p className="font-display text-[15px] font-bold tracking-tight">Enterprise plan inquiry</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="enterprise-modal-close"
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="relative flex-1 overflow-y-auto px-6 py-6">
          {submitState === "success" ? (
            <div className="text-center" data-testid="enterprise-success">
              <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/30">
                <Check className="h-7 w-7" />
              </span>
              <h3 className="mt-4 font-display text-2xl font-black tracking-tight">Got it — we&apos;re on it.</h3>
              <p className="mt-1.5 max-w-md text-sm leading-relaxed text-muted-foreground mx-auto">
                Our enterprise team will reach out within 1 business day to set up a tailored walkthrough,
                custom quote, and whitelabel review (if requested).
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 inline-flex h-11 items-center gap-1.5 rounded-xl bg-foreground px-5 text-sm font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="font-display text-2xl font-black leading-tight tracking-tight sm:text-3xl">
                Built for groups, multi-locations, and organizations.
              </p>
              <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted-foreground">
                Pricing is custom, scoped to your suite + locations + whitelabel preference, and finalized after a discovery call.
              </p>

              {/* Highlights */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {HIGHLIGHTS.map((h) => {
                  const Icon = h.icon;
                  return (
                    <div key={h.label} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5">
                      <span className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-secondary ${h.color}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-bold tracking-tight text-foreground">{h.label}</p>
                        <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">{h.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="enterprise-form">
                <Field label="Name" required>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Dr. Sarah Lee"
                    data-testid="enterprise-form-name"
                    className="ent-ipt"
                  />
                </Field>
                <Field label="Company / Group" required>
                  <input
                    type="text" required value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                    placeholder="ClinicGroup Holdings"
                    data-testid="enterprise-form-company"
                    className="ent-ipt"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@clinicgroup.com"
                    data-testid="enterprise-form-email"
                    className="ent-ipt"
                  />
                </Field>
                <Field label="Phone" optional>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+1 (555) 555-5555"
                    data-testid="enterprise-form-phone"
                    className="ent-ipt"
                  />
                </Field>
                <Field label="Number of locations" optional>
                  <select
                    value={form.locations}
                    onChange={(e) => setForm((f) => ({ ...f, locations: e.target.value }))}
                    data-testid="enterprise-form-locations"
                    className="ent-ipt"
                  >
                    <option value="">How many?</option>
                    <option value="2-5">2 – 5</option>
                    <option value="6-15">6 – 15</option>
                    <option value="16-50">16 – 50</option>
                    <option value="50+">50+</option>
                  </select>
                </Field>
                <Field label="Interested in whitelabel?" optional>
                  <select
                    value={form.whitelabel}
                    onChange={(e) => setForm((f) => ({ ...f, whitelabel: e.target.value }))}
                    data-testid="enterprise-form-whitelabel"
                    className="ent-ipt"
                  >
                    <option value="no">No, just Enterprise</option>
                    <option value="yes">Yes — explore whitelabel</option>
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Anything else we should know?" optional>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder="Required integrations, security needs, current EMR, target go-live, etc."
                      data-testid="enterprise-form-message"
                      className="ent-ipt resize-none"
                    />
                  </Field>
                </div>

                {submitState === "error" && (
                  <p data-testid="enterprise-form-error" className="col-span-full text-[12px] font-medium text-rose-500">{error}</p>
                )}

                <div className="col-span-full mt-2 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-[12px]">
                  <Crown className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-500" />
                  <p className="leading-snug text-foreground/85">
                    <span className="font-bold">Enterprise pricing is custom.</span>{" "}
                    <span className="text-muted-foreground">
                      Scoped to your locations + modules + whitelabel preference. Base $297 / $497 per location still applies — add-on cost is quoted on the discovery call.
                    </span>
                  </p>
                </div>

                <div className="col-span-full mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-4">
                  <p className="text-[11px] text-muted-foreground">
                    We&apos;ll respond within 1 business day. No spam.
                  </p>
                  <button
                    type="submit"
                    disabled={submitState === "loading"}
                    data-testid="enterprise-form-submit"
                    className="inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-5 text-[13px] font-bold text-white shadow-md shadow-amber-500/25 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
                  >
                    {submitState === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />}
                    {submitState === "loading" ? "Sending…" : "Talk to Enterprise"}
                    {submitState !== "loading" && <ArrowRight className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </form>

              <style>{`.ent-ipt{display:block;width:100%;border-radius:0.75rem;border-width:1px;border-color:hsl(var(--border));background-color:hsl(var(--background));padding:0.625rem 0.875rem;font-size:13px;font-weight:500;color:hsl(var(--foreground));outline:none}.ent-ipt:focus{border-color:rgb(245 158 11 / 0.55);box-shadow:0 0 0 2px rgb(245 158 11 / 0.20)}`}</style>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, optional, required, children }) => (
  <div>
    <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {label}
      {optional && <span className="ml-1 font-normal lowercase tracking-normal text-muted-foreground/60">(optional)</span>}
      {required && <span className="ml-1 text-rose-500">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);
